import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Search, 
  FileText, 
  Building2, 
  MapPin, 
  DollarSign, 
  Users,
  BarChart3,
  TrendingUp
} from 'lucide-react';
import Page from '../../components/Page';

interface SearchResult {
  id: string;
  title: string;
  description: string;
  category: string;
  icon: React.ComponentType<any>;
  href: string;
  keywords: string[];
}

const searchResults: SearchResult[] = [
  // NFe Results
  {
    id: 'nfes-overview',
    title: 'Visão Geral NFe',
    description: 'Dashboard principal com métricas gerais de notas fiscais, status e indicadores principais',
    category: 'NFe',
    icon: BarChart3,
    href: '/nfes/overview',
    keywords: ['nfe', 'nota fiscal', 'visão geral', 'dashboard nfe', 'overview']
  },
  {
    id: 'nfes-analysis',
    title: 'Análise NFe',
    description: 'Análise detalhada das notas fiscais com gráficos, tendências e insights',
    category: 'NFe',
    icon: BarChart3,
    href: '/nfes/analysis',
    keywords: ['nfe', 'análise', 'gráficos', 'insights', 'tendências']
  },
  {
    id: 'nfes-fiscal',
    title: 'Fiscal NFe',
    description: 'Informações fiscais, validações e conformidade das notas fiscais',
    category: 'NFe',
    icon: FileText,
    href: '/nfes/fiscal',
    keywords: ['nfe', 'fiscal', 'conformidade', 'validação', 'tributário']
  },

  // CTE Results
  {
    id: 'ctes-overview',
    title: 'Visão Geral CTE',
    description: 'Dashboard principal com métricas gerais de conhecimentos de transporte',
    category: 'CTE',
    icon: BarChart3,
    href: '/ctes/overview',
    keywords: ['cte', 'conhecimento transporte', 'visão geral', 'dashboard cte', 'overview']
  },
  {
    id: 'ctes-analysis',
    title: 'Análise CTE',
    description: 'Análise detalhada dos conhecimentos de transporte com métricas de performance',
    category: 'CTE',
    icon: BarChart3,
    href: '/ctes/analysis',
    keywords: ['cte', 'análise', 'performance', 'métricas', 'insights']
  },
  {
    id: 'ctes-routes',
    title: 'Rotas CTE',
    description: 'Análise de rotas, trajetos e otimização logística dos conhecimentos de transporte',
    category: 'CTE',
    icon: MapPin,
    href: '/ctes/routes',
    keywords: ['cte', 'rotas', 'trajetos', 'logística', 'otimização']
  },

  // Centros de Distribuição
  {
    id: 'cd-overview',
    title: 'Visão Geral CD',
    description: 'Dashboard principal dos centros de distribuição com métricas operacionais',
    category: 'Centros de Distribuição',
    icon: BarChart3,
    href: '/distribution-centers/overview',
    keywords: ['cd', 'centro distribuição', 'visão geral', 'dashboard cd', 'overview']
  },
  {
    id: 'cd-performance',
    title: 'Performance CD',
    description: 'Análise de performance, eficiência e produtividade dos centros de distribuição',
    category: 'Centros de Distribuição',
    icon: BarChart3,
    href: '/distribution-centers/performance',
    keywords: ['cd', 'performance', 'eficiência', 'produtividade', 'análise']
  },
  {
    id: 'cd-capacity',
    title: 'Capacidade CD',
    description: 'Análise de capacidade, utilização e gestão de estoque dos centros',
    category: 'Centros de Distribuição',
    icon: Building2,
    href: '/distribution-centers/capacity',
    keywords: ['cd', 'capacidade', 'utilização', 'estoque', 'gestão']
  },

  // Malha Logística
  {
    id: 'sales-force-analysis',
    title: 'Análise da Força de Venda',
    description: 'Análise completa da performance e eficiência da força de vendas',
    category: 'Malha Logística',
    icon: TrendingUp,
    href: '/logistics/sales-force-analysis',
    keywords: ['força de venda', 'vendas', 'performance', 'vendedores', 'análise', 'equipe']
  },
  {
    id: 'logistics-network',
    title: 'Rede Logística',
    description: 'Visualização da malha logística, conexões e infraestrutura de transporte',
    category: 'Malha Logística',
    icon: MapPin,
    href: '/logistics/network',
    keywords: ['malha', 'logística', 'rede', 'conexões', 'infraestrutura']
  },
  {
    id: 'logistics-routes',
    title: 'Rotas Logísticas',
    description: 'Análise de rotas, trajetos e otimização da malha logística',
    category: 'Malha Logística',
    icon: MapPin,
    href: '/logistics/routes',
    keywords: ['malha', 'rotas', 'trajetos', 'logística', 'otimização']
  },
  {
    id: 'logistics-optimization',
    title: 'Otimização Logística',
    description: 'Ferramentas de otimização, eficiência e melhoria da malha logística',
    category: 'Malha Logística',
    icon: BarChart3,
    href: '/logistics/optimization',
    keywords: ['malha', 'otimização', 'eficiência', 'melhoria', 'ferramentas']
  },

  // Análise de Custos
  {
    id: 'costs-overview',
    title: 'Visão Geral Custos',
    description: 'Dashboard principal de análise de custos operacionais e financeiros',
    category: 'Análise de Custos',
    icon: BarChart3,
    href: '/costs/overview',
    keywords: ['custos', 'análise', 'visão geral', 'dashboard custos', 'overview']
  },
  {
    id: 'costs-breakdown',
    title: 'Breakdown de Custos',
    description: 'Análise detalhada e breakdown dos custos por categoria e operação',
    category: 'Análise de Custos',
    icon: DollarSign,
    href: '/costs/breakdown',
    keywords: ['custos', 'breakdown', 'detalhamento', 'análise', 'categoria']
  },
  {
    id: 'costs-trends',
    title: 'Tendências de Custos',
    description: 'Análise de tendências, evolução histórica e projeções de custos',
    category: 'Análise de Custos',
    icon: BarChart3,
    href: '/costs/trends',
    keywords: ['custos', 'tendências', 'evolução', 'histórico', 'projeções']
  },

  // Carteira de Clientes
  {
    id: 'clients-overview',
    title: 'Visão Geral Clientes',
    description: 'Dashboard principal da carteira de clientes com métricas de relacionamento',
    category: 'Carteira de Clientes',
    icon: BarChart3,
    href: '/clients/overview',
    keywords: ['clientes', 'carteira', 'visão geral', 'dashboard clientes', 'overview']
  },
  {
    id: 'clients-segments',
    title: 'Segmentos de Clientes',
    description: 'Análise de segmentação, categorização e perfil da carteira de clientes',
    category: 'Carteira de Clientes',
    icon: Users,
    href: '/clients/segments',
    keywords: ['clientes', 'segmentos', 'segmentação', 'categorias', 'perfil']
  },
  {
    id: 'clients-performance',
    title: 'Performance Clientes',
    description: 'Análise de performance, resultados e métricas da carteira de clientes',
    category: 'Carteira de Clientes',
    icon: BarChart3,
    href: '/clients/performance',
    keywords: ['clientes', 'performance', 'resultados', 'métricas', 'análise']
  }
];

const SearchResultsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [filteredResults, setFilteredResults] = useState<SearchResult[]>([]);

  useEffect(() => {
    const searchQuery = searchParams.get('q') || '';
    setQuery(searchQuery);
    
    if (searchQuery.trim()) {
      // Busca mais simples e robusta
      const results = searchResults.filter(result => {
        const searchTerm = searchQuery.toLowerCase();
        
        // Busca em keywords
        const keywordMatch = result.keywords.some(keyword => 
          keyword.toLowerCase().includes(searchTerm)
        );
        
        // Busca em título
        const titleMatch = result.title.toLowerCase().includes(searchTerm);
        
        // Busca em descrição
        const descriptionMatch = result.description.toLowerCase().includes(searchTerm);
        
        // Busca em categoria
        const categoryMatch = result.category.toLowerCase().includes(searchTerm);
        
        return keywordMatch || titleMatch || descriptionMatch || categoryMatch;
      });
      
      setFilteredResults(results);
    } else {
      setFilteredResults([]);
    }
  }, [searchParams]);

  const handleResultClick = (href: string) => {
    navigate(href);
  };

  const groupedResults = filteredResults.reduce((acc, result) => {
    if (!acc[result.category]) {
      acc[result.category] = [];
    }
    acc[result.category].push(result);
    return acc;
  }, {} as Record<string, SearchResult[]>);

  return (
    <Page
      title="🔍 Resultados da Busca"
      subtitle={query ? `Resultados para "${query}"` : 'Digite algo para buscar'}
      onRefresh={() => window.location.reload()}
    >
      <div className="w-full h-full overflow-auto p-6">
        {query.trim() ? (
          <div className="space-y-6">
            {filteredResults.length > 0 ? (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <h2 
                    className="text-2xl font-bold mb-2"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    Encontrados {filteredResults.length} resultados para "{query}"
                  </h2>
                </div>
                
                {Object.entries(groupedResults).map(([category, results]) => (
                  <motion.div
                    key={category}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-4"
                  >
                    <h3 
                      className="text-xl font-semibold flex items-center"
                      style={{ color: 'var(--text-primary)' }}
                    >
                      <BarChart3 
                        className="h-5 w-5 mr-2" 
                        style={{ color: 'var(--primary)' }}
                      />
                      {category}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {results.map((result) => (
                        <motion.div
                          key={result.id}
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.2 }}
                          onClick={() => handleResultClick(result.href)}
                          className="group rounded-xl border p-6 cursor-pointer hover:shadow-xl transition-all duration-300 hover:scale-105"
                          style={{
                            backgroundColor: 'var(--bg-card)',
                            borderColor: 'var(--border-primary)'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = 'var(--primary)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = 'var(--border-primary)'
                          }}
                        >
                          <div className="flex flex-col h-full">
                            {/* Header com ícone e título */}
                            <div className="flex items-start space-x-4 mb-4">
                              <div className="flex-shrink-0">
                                <div 
                                  className="w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300"
                                  style={{
                                    background: 'linear-gradient(135deg, var(--bg-tertiary) 0%, var(--bg-tertiary) 100%)',
                                    opacity: 0.8
                                  }}
                                >
                                  <result.icon 
                                    className="h-6 w-6" 
                                    style={{ color: 'var(--primary)' }}
                                  />
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 
                                  className="text-lg font-semibold mb-1 transition-colors duration-300"
                                  style={{ color: 'var(--text-primary)' }}
                                >
                                  {result.title}
                                </h4>
                                <div className="flex items-center space-x-2">
                                  <span 
                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                                    style={{
                                      backgroundColor: 'var(--bg-tertiary)',
                                      color: 'var(--text-primary)',
                                      opacity: 0.8
                                    }}
                                  >
                                    {result.category}
                                  </span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Descrição */}
                            <div className="flex-1 mb-4">
                              <p 
                                className="text-sm leading-relaxed"
                                style={{ color: 'var(--text-secondary)' }}
                              >
                                {result.description}
                              </p>
                            </div>
                            
                            {/* Footer com ação */}
                            <div 
                              className="flex items-center justify-between pt-4 border-t"
                              style={{ borderColor: 'var(--border-primary)' }}
                            >
                              <span 
                                className="text-xs"
                                style={{ color: 'var(--text-tertiary)' }}
                              >
                                Clique para acessar
                              </span>
                              <div 
                                className="flex items-center group-hover:translate-x-1 transition-transform duration-300"
                                style={{ color: 'var(--primary)' }}
                              >
                                <span className="text-sm font-medium">Acessar</span>
                                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-12"
              >
                <Search 
                  className="h-16 w-16 mx-auto mb-4" 
                  style={{ color: 'var(--text-tertiary)' }}
                />
                <h3 
                  className="text-xl font-semibold mb-2"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Nenhum resultado encontrado para "{query}"
                </h3>
                <p 
                  style={{ color: 'var(--text-tertiary)' }}
                >
                  Tente usar termos diferentes ou mais específicos
                </p>
              </motion.div>
            )}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <Search 
              className="h-16 w-16 mx-auto mb-4" 
              style={{ color: 'var(--text-tertiary)' }}
            />
            <h3 
              className="text-xl font-semibold mb-2"
              style={{ color: 'var(--text-secondary)' }}
            >
              Digite algo para buscar
            </h3>
            <p 
              style={{ color: 'var(--text-tertiary)' }}
            >
              Use a barra de pesquisa no topo da página
            </p>
          </motion.div>
        )}
      </div>
    </Page>
  );
};

export default SearchResultsPage;
